import { resolveAssetUrl } from '../../../../utils/assetUrl'
import type {
  InDivisionRecord,
  StandingsEntry,
  StandingsRow,
  StandingsStat,
} from '../types/standings.types'

/** Stat id for the in-division record ("W-L-T-OTL-SOL"). */
export const IN_DIVISION_STAT_ID = '208'

/** Stat id for points, which the standings list is ordered by. */
export const POINTS_STAT_ID = '38'

/** Number of outcomes in a record string: W, L, T, OTL, SOL. */
const RECORD_PART_COUNT = 5

/**
 * Parse the API's in-division record string into its outcomes.
 *
 * Returns `null` for anything that does not describe at least one played game.
 * That includes the bare `"0"` the API sends for a team with no games — it is
 * NOT a 5-tuple, and rendering it as a zeroed record would fabricate a bar out
 * of nothing. Callers use `null` to pick the empty treatment.
 */
export const parseInDivisionRecord = (
  raw: unknown
): InDivisionRecord | null => {
  if (typeof raw !== 'string') return null

  const parts = raw.split('-')
  if (parts.length !== RECORD_PART_COUNT) return null

  const nums = parts.map(Number)
  // `Number('')` is 0, so an empty segment (from "1--1-0-0-0") would otherwise
  // slip through as a valid zero. Reject blanks explicitly.
  if (parts.some((part) => part.trim() === '')) return null
  if (nums.some((n) => !Number.isFinite(n) || n < 0)) return null

  const [wins, losses, ties, otLosses, soLosses] = nums
  const gamesPlayed = wins + losses + ties + otLosses + soLosses

  // An all-zero tuple means the same thing as "0": nothing has been played.
  if (gamesPlayed === 0) return null

  return { wins, losses, ties, otLosses, soLosses, gamesPlayed }
}

const findStat = (
  stats: StandingsStat[] | undefined,
  id: string
): StandingsStat | undefined => stats?.find((stat) => stat.id === id)

/**
 * Map raw standings entries to sorted, render-ready rows.
 *
 * Ordering: points descending, then games played descending as a tiebreak, with
 * every team that has no record last regardless of points — a placeholder row
 * ("Winner of Semi-final A") has nothing to compare and belongs at the bottom.
 */
export const toStandingsRows = (entries: unknown): StandingsRow[] => {
  if (!Array.isArray(entries)) return []

  return (entries as StandingsEntry[])
    .filter((entry) => entry?.team?.id)
    .map((entry) => {
      const pointsStat = findStat(entry.stats, POINTS_STAT_ID)
      const points = Number(pointsStat?.value ?? 0)

      return {
        teamId: entry.team.id,
        name: entry.team.name_full,
        abbreviation: entry.team.abbreviation ?? '',
        // Never render an API asset path directly — see the asset-URL rule.
        logo: resolveAssetUrl(entry.team.image),
        points: Number.isFinite(points) ? points : 0,
        record: parseInDivisionRecord(
          findStat(entry.stats, IN_DIVISION_STAT_ID)?.value
        ),
      }
    })
    .sort((a, b) => {
      if (!a.record !== !b.record) return a.record ? -1 : 1
      if (b.points !== a.points) return b.points - a.points
      return (b.record?.gamesPlayed ?? 0) - (a.record?.gamesPlayed ?? 0)
    })
}
